import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { useChatStore } from '../../stores/chatStore';
import { sendChatMessage } from '../../services/chat';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import type { ChatContext } from '@mise/shared';

interface ChatWidgetProps {
  context: Omit<ChatContext, 'restaurantId'>;
}

export default function ChatWidget({ context }: ChatWidgetProps) {
  const {
    isOpen,
    messages,
    isLoading,
    error,
    toggleOpen,
    addMessage,
    updateMessage,
    clearMessages,
    setLoading,
    setError,
  } = useChatStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && isOpen) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const handleSend = async (text: string) => {
    setError(null);
    setSuggestions([]);

    // Add user message
    const userMessageId = addMessage({
      text,
      sender: 'user',
      status: 'sent',
    });

    // Build conversation history from last 10 messages
    const history = messages.slice(-10).map((msg) => ({
      role: msg.sender as 'user' | 'assistant',
      content: msg.text,
    }));

    setLoading(true);

    // Add pending assistant message
    const assistantMessageId = addMessage({
      text: '',
      sender: 'assistant',
      status: 'sending',
    });

    try {
      const response = await sendChatMessage(text, context, history);

      if (response.success && response.data) {
        updateMessage(assistantMessageId, {
          text: response.data.response,
          status: 'sent',
        });

        if (response.data.suggestions) {
          setSuggestions(response.data.suggestions);
        }
      } else {
        updateMessage(assistantMessageId, {
          text: 'Sorry, I had trouble processing that. Please try again.',
          status: 'error',
        });
        setError(response.error?.message || 'Failed to get response');
      }
    } catch (err) {
      updateMessage(assistantMessageId, {
        text: 'Sorry, something went wrong. Please try again.',
        status: 'error',
      });
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* FAB Button - hidden when chat is open on mobile */}
      <button
        onClick={toggleOpen}
        className={clsx(
          'fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all',
          isOpen
            ? 'hidden sm:flex bg-slate-600 hover:bg-slate-700'
            : 'bg-primary-600 hover:bg-primary-700'
        )}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 left-2 right-2 sm:left-auto sm:right-4 sm:bottom-36 z-40 sm:w-96 max-h-[calc(100dvh-6rem)] sm:max-h-[32rem] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
          {/* Header */}
          <div className="bg-primary-600 text-white px-4 py-3 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Mise Assistant</h3>
              <p className="text-xs text-primary-200">Ask about the menu</p>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button
                  onClick={clearMessages}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={toggleOpen}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  Ask me about menu items, ingredients, allergens, or pairings!
                </p>
              </div>
            ) : (
              messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))
            )}

            {/* Loading indicator */}
            {isLoading && messages[messages.length - 1]?.status === 'sending' && (
              <div className="flex items-center gap-2 text-slate-500 text-sm ml-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span>Thinking...</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error message */}
          {error && (
            <div className="px-4 py-2 bg-danger-50 text-danger-700 text-sm border-t border-danger-100">
              {error}
            </div>
          )}

          {/* Input */}
          <ChatInput
            onSend={handleSend}
            disabled={isLoading}
            suggestions={suggestions}
          />
        </div>
      )}
    </>
  );
}
